import {ShippingMethod} from "@/lib/types/shippingMethod";

export const normalizeShippingMethods = (data: unknown): ShippingMethod[] => {
  if (Array.isArray(data)) {
    return data;
  }

  if (data && typeof data === 'object' && 'hydra:member' in data) {
    const member = (data as { 'hydra:member'?: ShippingMethod[] })['hydra:member'];
    return Array.isArray(member) ? member : [];
  }

  return [];
};

const SHIPPING_METHOD_LABELS: Record<string, string> = {
  FEDEX_GROUND: 'FedEx Ground',
  PARTYSUPPLIES_FEDEX_GROUND: 'FedEx Ground',
  FEDEX_INTERNATIONAL_PRIORITY: 'FedEx International Priority',
  FEDEX_INTERNATIONAL_ECONOMY: 'FedEx International Economy',
  FEDEX_PRIORITY_OVERNIGHT: 'FedEx Priority Overnight',
  PARTYSUPPLIES_FEDEX_PRIORITY_OVERNIGHT: 'FedEx Priority Overnight',
  FEDEX_2_DAY: 'FedEx 2-Day',
};

export const PARTY_SUPPLIES_DOMESTIC_FEDEX_GROUND = 'PARTYSUPPLIES_FEDEX_GROUND';
export const PARTY_SUPPLIES_DOMESTIC_FEDEX_PRIORITY_OVERNIGHT = 'PARTYSUPPLIES_FEDEX_PRIORITY_OVERNIGHT';

const LEGACY_SPECS_TO_PARTY_METHOD: Record<string, string> = {
  FEDEX_GROUND: PARTY_SUPPLIES_DOMESTIC_FEDEX_GROUND,
  FEDEX_PRIORITY_OVERNIGHT: PARTY_SUPPLIES_DOMESTIC_FEDEX_PRIORITY_OVERNIGHT,
};

const LEGACY_DOMESTIC_GROUND_CODES = [
  PARTY_SUPPLIES_DOMESTIC_FEDEX_GROUND,
  'FEDEX_GROUND',
] as const;

export const getShippingMethodDisplayName = (code: string): string => {
  const normalizedCode = code.includes('/')
    ? code.split('/').pop() ?? code
    : code;

  return SHIPPING_METHOD_LABELS[normalizedCode] ?? normalizedCode.replace(/_/g, ' ');
};

/** Ensure the method already applied to the order appears in the radio list. */
export const mergeAppliedShippingMethod = (
  methods: ShippingMethod[] = [],
  appliedCode?: string | null,
  shippingTotalDollars?: number | null,
): ShippingMethod[] => {
  if (!appliedCode) {
    return methods;
  }

  let resolvedAppliedCode = appliedCode;
  const partyReplacement = LEGACY_SPECS_TO_PARTY_METHOD[appliedCode];
  if (
    partyReplacement
    && methods.some((method) => method.code === partyReplacement)
  ) {
    resolvedAppliedCode = partyReplacement;
  }

  const appliedPriceCents = shippingTotalDollars == null
    ? null
    : Math.round(shippingTotalDollars * 100);
  const existingIndex = methods.findIndex((method) => method.code === resolvedAppliedCode);

  if (existingIndex >= 0) {
    if (appliedPriceCents == null || methods[existingIndex].price === appliedPriceCents) {
      return methods;
    }

    const updatedMethods = [...methods];
    updatedMethods[existingIndex] = {
      ...methods[existingIndex],
      price: appliedPriceCents,
    };
    return updatedMethods;
  }

  return [
    {
      code: resolvedAppliedCode,
      name: getShippingMethodDisplayName(resolvedAppliedCode),
      description: null,
      price: appliedPriceCents ?? 0,
    },
    ...methods,
  ];
};

function shippingMethodsSignature(methods: ShippingMethod[]): string {
  return methods
    .map((method) => `${method.code}:${method.price ?? 0}`)
    .sort()
    .join('|');
}

/** Keep the current list during refetch when Sylius returns an empty or unchanged snapshot. */
export function shouldReplaceAvailableShippingMethods(
  current: ShippingMethod[],
  next: ShippingMethod[],
  isFirstLoad: boolean,
): boolean {
  if (isFirstLoad) {
    return true;
  }

  if (next.length === 0 && current.length > 0) {
    return false;
  }

  return shippingMethodsSignature(current) !== shippingMethodsSignature(next);
}

/** Hide legacy SPECS FedEx methods when the Party Supplies equivalents are available. */
export const resolvePartySuppliesShippingMethodsForDisplay = (
  methods: ShippingMethod[] = [],
  appliedCode?: string | null,
  shippingTotalDollars?: number | null,
): ShippingMethod[] => {
  const merged = mergeAppliedShippingMethod(methods, appliedCode, shippingTotalDollars);
  const partyMethodCodes = new Set(merged.map((method) => method.code));

  return merged.filter((method) => {
    const partyReplacement = LEGACY_SPECS_TO_PARTY_METHOD[method.code];
    if (!partyReplacement) {
      return true;
    }

    return !partyMethodCodes.has(partyReplacement);
  });
};

/** Default method code when estimate assigns shipping without fetching the methods list. */
export function resolveDefaultPartyShippingMethodCode(countryCode = 'US'): string {
  if (countryCode.toUpperCase() !== 'US') {
    return 'FEDEX_INTERNATIONAL_PRIORITY';
  }

  return PARTY_SUPPLIES_DOMESTIC_FEDEX_GROUND;
}

/** Resolve the shipping method to apply at checkout finalize. */
export function resolveCheckoutShippingMethodCode(
  methods: ShippingMethod[],
  selectedMethodCode: string | null | undefined,
  countryCode = 'US',
): string {
  const requested = selectedMethodCode?.trim();

  if (requested) {
    const exactMatch = methods.find((method) => method.code === requested);
    if (exactMatch) {
      return exactMatch.code;
    }

    const partyReplacement = LEGACY_SPECS_TO_PARTY_METHOD[requested];
    if (partyReplacement && methods.some((method) => method.code === partyReplacement)) {
      return partyReplacement;
    }
  }

  const preferredMethod = findPreferredPartyShippingMethod(methods, countryCode);
  if (preferredMethod?.code) {
    return preferredMethod.code;
  }

  if (methods.length > 0) {
    return methods[0].code;
  }

  return requested || resolveDefaultPartyShippingMethodCode(countryCode);
}

/** Party Supplies ships via FedEx; default estimate/checkout selection prefers Ground. */
export const findPreferredPartyShippingMethod = (arr: ShippingMethod[] = [], countryCode = 'US') => {
  const country = countryCode.toUpperCase();
  const isInternational = country !== 'US';

  if (isInternational) {
    const intlPriority = arr.find((item) => item.code === 'FEDEX_INTERNATIONAL_PRIORITY');
    if (intlPriority) return intlPriority;

    const intlEconomy = arr.find((item) => item.code === 'FEDEX_INTERNATIONAL_ECONOMY');
    if (intlEconomy) return intlEconomy;
  }

  const domesticGround = LEGACY_DOMESTIC_GROUND_CODES
    .map((code) => arr.find((item) => item.code === code))
    .find(Boolean);
  if (domesticGround) return domesticGround;

  const fedex = arr.find((item) => item.code?.startsWith('FEDEX') || item.code?.startsWith('PARTYSUPPLIES_FEDEX'));
  if (fedex) return fedex;

  return arr[0] ?? null;
};

export const findPreferredService = (arr: ShippingMethod[] = []) => {
  const uber = arr.find(item => item.code === "UBER_DIRECT");
  if (uber) return uber;

  const doordash = arr.find(item => item.code === "DOORDASH_DRIVE");
  if (doordash) return doordash;

  return null;
};
